import { FC, useEffect } from "react";

import { Box, Stack } from "@mui/material";
import { PublicRoutes } from "@simplyk/common";
import Link from "next/link";
import { useSearchParams } from "next/navigation";

import { AmplitudeEvents } from "../../../../../../common/constants/amplitude";
import { useAmplitude } from "../../../../../../common/hooks/amplitude/useAmplitude";
import { useMediaQuery } from "../../../../../../common/hooks/useMediaQuery";
import { useTranslate } from "../../../../../../common/hooks/useTranslate";
import { useReferredByOrganizationId } from "../../hooks/useReferredByOrganizationId";

import { EmailSignUpForm } from "./components/EmailSignUpForm";

import { trpc } from "@/common/helpers/trpc";
import { OrDivider } from "@/components/OrDivider/OrDivider";
import { DashboardTypography } from "@/dashboard-design-system/Typography/DashboardTypography";
import { RegisterFormStructure } from "@/features/Auth/Register/components/RegisterFormStructure";
import { PendingReferralStore } from "@/features/Auth/Register/store/PendingReferralStore";
import { SignUpSessionStorage } from "@/features/Auth/Register/store/SignUpSessionStorage";
import { RegisterCategory } from "@/features/Auth/Register/types";
import { StytchGoogleButton } from "@/features/Auth/sharedComponents/StytchGoogleButton";

interface SignUpMethodChoiceStepProps {
  category?: RegisterCategory;
  isPendingShowcaseRedirect?: boolean;
}

export const SignUpMethodChoiceStep: FC<SignUpMethodChoiceStepProps> = ({ isPendingShowcaseRedirect = false }) => {
  const { t } = useTranslate();
  const { isSmallScreen } = useMediaQuery();
  const { logAmplitudeEvent } = useAmplitude();
  const { referredByOrganizationId } = useReferredByOrganizationId();
  const searchParams = useSearchParams();
  const referralLinkOrganizationId = searchParams?.get("referredByOrganizationId");

  const { data: getReferredOrganization } = trpc.getReferredOrganization.useQuery(
    { organizationId: referredByOrganizationId as string },
    { enabled: !!referredByOrganizationId }
  );

  useEffect(() => {
    if (isPendingShowcaseRedirect) {
      return;
    }
    SignUpSessionStorage.clear();
    logAmplitudeEvent(AmplitudeEvents.SignUpLandingPageViewed);
  }, [isPendingShowcaseRedirect, logAmplitudeEvent]);

  // Signing in instead of signing up leaves the sign-up session, so the referrer must outlive it.
  useEffect(() => {
    if (referralLinkOrganizationId) {
      PendingReferralStore.store(referralLinkOrganizationId);
    }
  }, [referralLinkOrganizationId]);

  const tagline = t("register", "signup.tagline");

  return (
    <RegisterFormStructure zeffyLogo={{ mobile: "center", desktop: "center" }} showSocialProof>
      <Box
        inert={isPendingShowcaseRedirect}
        sx={() => ({
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: "100%",
          margin: "0 auto",
          width: "100%",
        })}
      >
        <Stack gap={3} width="100%" paddingX={{ xs: 0.5, md: 2, lg: 3 }}>
          {/* Header */}
          <Box sx={{ textAlign: "center", mb: 2 }}>
            <DashboardTypography variant="h3">{t("register", "signup.startFundraising")}</DashboardTypography>

            <DashboardTypography variant="body2" vibe="text-brand-moderate">
              {getReferredOrganization?.name
                ? t("register", "referral.invitationToUseZeffy", { orgName: getReferredOrganization.name })
                : tagline}
            </DashboardTypography>
          </Box>

          {/* Google Sign Up Button */}
          <StytchGoogleButton label="signup" />

          {/* Divider */}
          <OrDivider />

          {/* Email Sign Up Form */}
          <EmailSignUpForm />

          {/* Login Link */}
          <Box sx={{ textAlign: "center", mt: isSmallScreen ? 1 : 2 }}>
            <DashboardTypography variant="body2" vibe="neutralVariant-20">
              {t("register", "signup.redirectToConnection1bis")}{" "}
              <Link href={PublicRoutes.SignIn} passHref>
                <DashboardTypography
                  component="a"
                  variant="body2"
                  sx={(theme) => ({
                    color: theme.palette.primary.main,
                    textDecoration: "none",
                    "&:hover": {
                      textDecoration: "underline",
                    },
                  })}
                >
                  {t("register", "signup.redirectToConnection2")}
                </DashboardTypography>
              </Link>
            </DashboardTypography>
          </Box>
        </Stack>
      </Box>
    </RegisterFormStructure>
  );
};
